import { ChevronLeft } from 'lucide-react'
import { Link, PageWidthButton } from '../../core/index.js'

export interface ConfigTabItem<Id extends string> {
  id: Id
  label: string
  /** Marked with a coral dot while the tab has changes not yet saved. */
  unsaved?: boolean
}

/**
 * The Configuration page's tabs as the shell's bar (facility operations dashboard plan, mockup screen 3): a full-width
 * white row of underlined tabs that takes the place of the Dashboard's bar while the configuration is open, with a way
 * back to where the reader came from at its start. A long list scrolls sideways rather than wrapping.
 */
export function ConfigTabsBar<Id extends string>({ tabs, active, onChange, back }: { tabs: readonly ConfigTabItem<Id>[]; active: Id; onChange: (id: Id) => void; back: { href: string; label: string } }) {
  return (
    <nav aria-label="Configuration" className="bg-white border-b border-marine/15">
      <div className="max-w-7xl mx-auto flex items-center gap-1 px-7 overflow-x-auto">
        <Link to={back.href} className="inline-flex shrink-0 items-center gap-1 pr-3 mr-1 border-r border-marine/15 text-xs font-bold text-marine hover:text-marine-dk">
          <ChevronLeft className="h-4 w-4" />
          {back.label}
        </Link>
        <div role="tablist" className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active === tab.id}
              onClick={() => onChange(tab.id)}
              className={`shrink-0 whitespace-nowrap text-xs font-bold px-2.5 py-3 border-b-2 ${active === tab.id ? 'text-marine border-coral' : 'text-ink-mid border-transparent hover:text-marine'}`}
            >
              {tab.label}
              {tab.unsaved && <span aria-label="Unsaved changes" title="Unsaved changes" className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-coral align-middle" />}
            </button>
          ))}
        </div>
        <span className="flex-1" />
        <PageWidthButton />
      </div>
    </nav>
  )
}
